import { Anchor, Box, Card, Paragraph, Text, TextButton } from '@zillow/constellation-10';
import { getLatestMessageDateText, UnreadIndicator } from '@zg-rentals/satellite-components';
import { SatelliteConversationMessageType } from '@zg-rentals/ts-satellite-client';
import type { SatelliteWebLandlordLeadManagementTableRowResponseDto } from '@zg-rentals/ts-satellite-client';
import Link from 'next/link';
import { forwardRef, useContext, useEffect } from 'react';
import getLeadManagementMessageText from '../../../utils/getLeadManagementMessageText';
import { InboxPageMobileView } from '../../../types/InboxPageMobileView';
import { LeadManagementContext } from '../../../contexts/LeadManagementContextProvider';
import useLeadManagementActions from '../../../hooks/useLeadManagementActions';
import StringConstants from '../../../constants/StringConstants';

export type RenterDetailsLatestContactProps = {
  lead: SatelliteWebLandlordLeadManagementTableRowResponseDto | null;
  conversationId: string;
  isBackgroundHighlighted: boolean;
};

const RenterDetailsLatestContact = forwardRef<HTMLElement, RenterDetailsLatestContactProps>(
  ({ lead, conversationId, isBackgroundHighlighted }, ref): JSX.Element => {
    const { isArchivedFolder, isReportedFolder } = useContext(LeadManagementContext);
    const listingAlias = lead?.listingDetails?.listingAlias ?? '';
    const { handleMarkAsRead, handleViewMessageClick } = useLeadManagementActions({ conversationId });

    const { latestContact, status } = lead || {};
    const { messageType, messageDateMs, messageText, isMessageOwner, isPhoneLead, isRead, senderName } =
      latestContact || {};
    const { isConversationBlockedByZillow = false } = status || {};
    const timestamp = messageDateMs ? getLatestMessageDateText(String(messageDateMs)) : '';
    const displayName = isMessageOwner ? 'You' : senderName || '';
    const messagePrefix =
      messageText && messageType === SatelliteConversationMessageType.LISTING_MESSAGE && displayName
        ? `${displayName}: `
        : '';
    const message = getLeadManagementMessageText({
      isConversationBlockedByZillow,
      messageText,
    });

    useEffect(() => {
      if (isBackgroundHighlighted && !isRead && messageDateMs) {
        handleMarkAsRead(String(messageDateMs));
      }
      // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
    }, [isBackgroundHighlighted]);

    return (
      <>
        <Paragraph textStyle="body-sm-bold" css={{ marginTop: 'loose' }}>
          Latest Contact
        </Paragraph>
        <Card
          elevated={false}
          css={{
            marginTop: 'default',
            padding: 'loose',
            backgroundColor: isBackgroundHighlighted ? 'blue.100' : 'white',
          }}
        >
          <Paragraph textStyle="body-xs" css={{ marginBottom: 'tighter' }}>
            {timestamp}
          </Paragraph>
          {isPhoneLead ? (
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              {!isRead && <UnreadIndicator css={{ marginRight: 'tightest' }} />}
              <Paragraph textStyle={isRead ? 'body-sm' : 'body-sm-bold'}>Voice message</Paragraph>
            </Box>
          ) : (
            <Box
              css={{
                overflow: 'hidden',
                lineClamp: 3,
              }}
            >
              {!isRead && <UnreadIndicator css={{ marginRight: 'tightest' }} />}
              <Text textStyle={isRead ? 'body-sm' : 'body-sm-bold'} css={{ textTransform: 'capitalize' }}>
                {messagePrefix}
              </Text>
              <Text textStyle="body-sm" ref={ref}>
                {message}
                {isConversationBlockedByZillow && (
                  <>
                    &nbsp;
                    <Anchor href={StringConstants.CONVERSATION_BLOCKED_BY_ZILLOW_LINK} target="_blank">
                      View policy
                    </Anchor>
                  </>
                )}
              </Text>
            </Box>
          )}
          {isArchivedFolder || isReportedFolder ? (
            <TextButton textStyle="body-sm-bold" disabled css={{ marginTop: 'tighter' }}>
              {StringConstants.VIEW_IN_MESSAGES}
            </TextButton>
          ) : (
            <TextButton
              textStyle="body-sm-bold"
              asChild
              onClick={handleViewMessageClick}
              css={{ marginTop: 'tighter' }}
            >
              <Link
                href={{
                  pathname: '/inbox/[[...conversationSlug]]',
                  query: {
                    conversationSlug: [listingAlias, conversationId],
                    mobileView: InboxPageMobileView.MessageList,
                  },
                }}
              >
                {StringConstants.VIEW_IN_MESSAGES}
              </Link>
            </TextButton>
          )}
        </Card>
      </>
    );
  },
);

RenterDetailsLatestContact.displayName = 'RenterDetailsLatestContact';
export default RenterDetailsLatestContact;
